import { Camera, Contrast, Move, Palette, PersonStanding, RotateCw, ZoomIn } from 'lucide-react';
import { useCallback, useEffect, useState, type ComponentType } from 'react';

import { Color3DToolButton } from './color-3d/color-3d-tool-button';
import { WindowLevelToolButton } from './window-level/window-level-tool-button';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useVisualization } from '@/context/visualization-context';
import { useViewportSnapshot } from '@/hooks/use-viewport-snapshot';
import { cn } from '@/lib/utils';
import type { ToolActivatedPayload } from '@/visualization/interaction';
import { CarmViewService } from '@/visualization/services/carm-view-service';
import { CarmRotateTool, ContrastTool, PanTool, RotateTool, ZoomTool } from '@/visualization/tools';
import type { PrimaryToolChangedPayload } from '@/visualization/tools/tool-group-manager';
import { ToolGroupManager } from '@/visualization/tools/tool-group-manager';
import { ViewportType } from '@/visualization/viewports/types';
import { getViewportCategory, ViewportCategory } from '@/visualization/viewports/viewport-categories';

interface ToolDefinition {
    toolId: string;
    label: string;
    icon: ComponentType<{ className?: string }>;
    description: string;
    /**
     * Popover this entry opens next to its activate button; absent = plain icon
     * button. ContrastTool drives both 2D and 3D, so the tool id no longer says
     * which control panel belongs to the viewport — the tool list does.
     */
    popover?: 'window-level' | 'color-3d';
}

const TOOLS_3D: ToolDefinition[] = [
    { toolId: RotateTool.toolName, label: 'Rotate', icon: RotateCw, description: 'Rotate camera' },
    { toolId: PanTool.toolName, label: 'Pan', icon: Move, description: 'Pan camera' },
    { toolId: ZoomTool.toolName, label: 'Zoom', icon: ZoomIn, description: 'Zoom in/out' },
    {
        toolId: ContrastTool.toolName,
        label: '3D Colors',
        icon: Palette,
        description: 'Adjust 3D colors',
        popover: 'color-3d',
    },
];

// While a C-arm draft is active the toolbar goes modal: the Rotate button
// drives the constrained C-arm rotation and Pan is unavailable (it would
// move the isocenter, silently changing the committed view)
const TOOLS_3D_CARM_EDIT: ToolDefinition[] = [
    { toolId: CarmRotateTool.toolName, label: 'Rotate', icon: RotateCw, description: 'Rotate C-arm' },
    { toolId: ZoomTool.toolName, label: 'Zoom', icon: ZoomIn, description: 'Zoom in/out' },
];

const WINDOW_LEVEL_TOOL: ToolDefinition = {
    toolId: ContrastTool.toolName,
    label: 'W/L',
    icon: Contrast,
    description: 'Adjust window/level',
    popover: 'window-level',
};

const TOOLS_2D: ToolDefinition[] = [
    WINDOW_LEVEL_TOOL,
    { toolId: PanTool.toolName, label: 'Pan', icon: Move, description: 'Pan camera' },
    { toolId: ZoomTool.toolName, label: 'Zoom', icon: ZoomIn, description: 'Zoom in/out' },
];

const TOOLS_CPR: ToolDefinition[] = [WINDOW_LEVEL_TOOL];

export interface ViewportToolbarProps {
    /** Viewport slot ID used to look up the ToolGroup */
    viewportId: string;
    /** Viewport type to determine which tools to show */
    viewportType: ViewportType;
}

function toolsForViewport(viewportType: ViewportType): ToolDefinition[] {
    switch (getViewportCategory(viewportType)) {
        case ViewportCategory.VOLUME_3D:
            return TOOLS_3D;
        case ViewportCategory.SLICE_2D:
            return TOOLS_2D;
        case ViewportCategory.CPR:
            return TOOLS_CPR;
    }
}

/**
 * Per-viewport toolbar for selecting the primary (left-click) tool.
 * Renders as a compact horizontal strip of icon buttons overlaid on the viewport.
 *
 * Calls InteractionService.setPrimaryTool() and listens to TOOL_ACTIVATED
 * events to keep the visual state in sync.
 */
export function ViewportToolbar({ viewportId, viewportType }: ViewportToolbarProps) {
    const { getInteractionService, getViewportService, getServiceManager } = useVisualization();
    const { captureDraft } = useViewportSnapshot();

    const is3D = viewportType === ViewportType.VOLUME_3D;
    const defaultToolId = is3D ? RotateTool.toolName : ContrastTool.toolName;

    // null while an edit mode owns the primary button (no tool highlighted)
    const [activeToolId, setActiveToolId] = useState<string | null>(defaultToolId);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isCarmEditActive, setIsCarmEditActive] = useState(false);

    const tools = is3D && isCarmEditActive ? TOOLS_3D_CARM_EDIT : toolsForViewport(viewportType);

    // Go modal while a C-arm draft is active. Lifecycle events only — not
    // useCarmEdit(), which re-renders on every EDIT_CHANGED drag frame.
    useEffect(() => {
        if (!is3D) return;

        const carmViewService = getServiceManager().getService<CarmViewService>(CarmViewService.SERVICE_NAME);

        // Re-read the draft in every handler instead of hardcoding true/false:
        // EDIT_STARTED can self-cancel synchronously (no 3D viewport), in which
        // case EDIT_CANCELLED may reach other listeners before this one runs
        const sync = () => setIsCarmEditActive(carmViewService.getDraft() !== null);
        sync();

        const unsubs = [
            carmViewService.subscribe(carmViewService.EVENTS.EDIT_STARTED, sync),
            carmViewService.subscribe(carmViewService.EVENTS.EDIT_COMMITTED, sync),
            carmViewService.subscribe(carmViewService.EVENTS.EDIT_CANCELLED, sync),
        ];
        return () => unsubs.forEach((unsub) => unsub());
    }, [getServiceManager, is3D]);

    // Subscribe to TOOL_ACTIVATED to stay in sync with sidebar and other toolbars
    useEffect(() => {
        const interactionService = getInteractionService();
        if (!interactionService) return;

        const unsub = interactionService.subscribe(
            interactionService.EVENTS.TOOL_ACTIVATED,
            (payload: ToolActivatedPayload) => {
                // Update if this specific viewport was targeted, or all viewports were changed
                if (payload.viewportId === viewportId || payload.viewportId === null) {
                    setActiveToolId(payload.toolId);
                }
            }
        );

        return unsub;
    }, [getInteractionService, viewportId]);

    // Track the primary binding directly: when an edit mode (C-arm) takes the
    // button with a non-selectable tool, no toolbar button matches it
    useEffect(() => {
        const toolGroupManager = getServiceManager().getService<ToolGroupManager>(
            ToolGroupManager.SERVICE_NAME
        );

        return toolGroupManager.subscribe(
            toolGroupManager.EVENTS.PRIMARY_TOOL_CHANGED,
            (payload: PrimaryToolChangedPayload) => {
                if (payload.viewportId === viewportId) setActiveToolId(payload.toolId);
            }
        );
    }, [getServiceManager, viewportId]);

    const handleToolChange = useCallback(
        (toolId: string) => {
            const interactionService = getInteractionService();
            if (!interactionService) return;

            interactionService.setPrimaryTool(viewportId, toolId);
        },
        [viewportId, getInteractionService]
    );

    const handleResetCamera = useCallback(() => {
        getViewportService().resetCamera(viewportId);
    }, [getViewportService, viewportId]);

    const handleCapture = useCallback(async () => {
        setIsCapturing(true);
        try {
            await captureDraft(viewportId);
        } catch (error) {
            console.warn('ViewportToolbar: snapshot capture failed:', error);
        } finally {
            setIsCapturing(false);
        }
    }, [captureDraft, viewportId]);

    if (tools.length === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                'absolute bottom-2 left-2 z-20 pointer-events-auto',
                'flex items-center gap-0.5',
                'rounded-md border border-border/50 bg-background/80 shadow-sm backdrop-blur-sm',
                'p-0.5'
            )}
        >
            {tools.map((tool) => {
                const isActive = activeToolId === tool.toolId;

                if (tool.popover === 'window-level') {
                    return (
                        <WindowLevelToolButton
                            key={tool.toolId}
                            isActive={isActive}
                            onActivate={() => handleToolChange(tool.toolId)}
                            icon={tool.icon}
                            description={tool.description}
                        />
                    );
                }

                if (tool.popover === 'color-3d') {
                    return (
                        <Color3DToolButton
                            key={tool.toolId}
                            isActive={isActive}
                            onActivate={() => handleToolChange(tool.toolId)}
                            icon={tool.icon}
                            description={tool.description}
                        />
                    );
                }

                const Icon = tool.icon;
                return (
                    <Tooltip key={tool.toolId}>
                        <TooltipTrigger asChild>
                            <Button
                                variant={isActive ? 'default' : 'ghost'}
                                size="icon-sm"
                                onClick={() => handleToolChange(tool.toolId)}
                                className={cn(
                                    'h-7 w-7',
                                    !isActive && 'text-muted-foreground hover:text-foreground'
                                )}
                            >
                                <Icon className="h-3.5 w-3.5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{tool.description}</TooltipContent>
                    </Tooltip>
                );
            })}

            {/* Action buttons */}
            <div className="ml-0.5 flex items-center gap-0.5 border-l border-border/50 pl-0.5">
                {is3D && !isCarmEditActive && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={handleResetCamera}
                                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            >
                                <PersonStanding className="h-3.5 w-3.5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">Frontal View</TooltipContent>
                    </Tooltip>
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={handleCapture}
                            disabled={isCapturing}
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        >
                            <Camera className="h-3.5 w-3.5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Capture snapshot</TooltipContent>
                </Tooltip>
            </div>
        </div>
    );
}
